import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { cellToLatLng } from 'h3-js';
import type { PeriodKey } from './seedHeat';

export type HeatCell = {
  id: string;
  h3Index: string;
  position: [number, number];
  probability: number;
  confidence: number;
  predictedDensity: number;
  observedDensity: number;
  sampleSize: number;
  bucketStart: string;
  source: 'supabase';
};

export type LiveEvent = {
  id: string;
  eventId: string;
  title: string;
  category: string;
  tags: string[];
  startsAt: string;
  endsAt: string | null;
  venueName: string | null;
  neighborhood: string | null;
  address: string | null;
  position: [number, number] | null;
  priceMin: number | null;
  priceMax: number | null;
  isFree: boolean;
  ticketUrl: string | null;
  officialUrl: string | null;
  imageUrl: string | null;
  probability: number;
  confidence: number;
  heatScore: number;
  sourceCount: number;
};

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

export const supabase: SupabaseClient | null = url && key ? createClient(url, key) : null;

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function periodRange(period: PeriodKey, now = new Date()) {
  if (period === 'agora') {
    return {
      start: new Date(now.getTime() - 60 * 60 * 1000),
      end: new Date(now.getTime() + 3 * 60 * 60 * 1000),
    };
  }

  const today = startOfLocalDay(now);
  if (period === 'hoje') {
    return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
  }

  if (period === 'amanha') {
    const start = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    return { start, end: new Date(start.getTime() + 24 * 60 * 60 * 1000) };
  }

  const weekday = today.getDay();
  const daysUntilSaturday = (6 - weekday + 7) % 7;
  const saturday = new Date(today.getTime() + daysUntilSaturday * 24 * 60 * 60 * 1000);
  return {
    start: saturday,
    end: new Date(saturday.getTime() + 2 * 24 * 60 * 60 * 1000),
  };
}

async function getCityId(citySlug: string): Promise<string | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('oqf_cities')
    .select('id')
    .eq('slug', citySlug)
    .eq('enabled', true)
    .maybeSingle();

  if (error || !data) return null;
  return String(data.id);
}

type RawHeatRow = {
  id: string | number;
  h3_index: string;
  bucket_start: string;
  predicted_density: number | string | null;
  observed_density: number | string | null;
  probability: number | string | null;
  confidence: number | string | null;
  sample_size: number | string | null;
};

function aggregateHeatRows(rows: RawHeatRow[]): HeatCell[] {
  type Aggregate = {
    id: string;
    h3Index: string;
    position: [number, number];
    probability: number;
    confidenceWeighted: number;
    confidenceWeight: number;
    predictedDensity: number;
    observedDensity: number;
    sampleSize: number;
    bucketStart: string;
  };

  const groups = new Map<string, Aggregate>();

  for (const row of rows) {
    try {
      const h3Index = String(row.h3_index);
      const [lat, lng] = cellToLatLng(h3Index);
      const probability = Number(row.probability ?? 0);
      const confidence = Number(row.confidence ?? 0);
      const predicted = Math.max(0, Number(row.predicted_density ?? 0));
      const observed = Math.max(0, Number(row.observed_density ?? 0));
      const sampleSize = Math.max(0, Number(row.sample_size ?? 0));
      const weight = Math.max(predicted, observed, sampleSize, 1);
      const existing = groups.get(h3Index);

      if (!existing) {
        groups.set(h3Index, {
          id: String(row.id),
          h3Index,
          position: [lng, lat],
          probability,
          confidenceWeighted: confidence * weight,
          confidenceWeight: weight,
          predictedDensity: predicted,
          observedDensity: observed,
          sampleSize,
          bucketStart: String(row.bucket_start ?? ''),
        });
        continue;
      }

      // Probability represents "how likely is this cell to be hot during the selected period".
      // Max is preferable to summing probabilities across hourly buckets.
      existing.probability = Math.max(existing.probability, probability);
      existing.confidenceWeighted += confidence * weight;
      existing.confidenceWeight += weight;
      existing.predictedDensity += predicted;
      existing.observedDensity += observed;
      existing.sampleSize += sampleSize;
      if (String(row.bucket_start) < existing.bucketStart) existing.bucketStart = String(row.bucket_start);
    } catch {
      // Ignore malformed H3 rows rather than breaking the entire city map.
    }
  }

  return [...groups.values()].map((group) => ({
    id: group.id,
    h3Index: group.h3Index,
    position: group.position,
    probability: group.probability,
    confidence: group.confidenceWeight > 0
      ? Math.min(1, group.confidenceWeighted / group.confidenceWeight)
      : 0,
    predictedDensity: Math.round(group.predictedDensity),
    observedDensity: Math.round(group.observedDensity),
    sampleSize: Math.round(group.sampleSize),
    bucketStart: group.bucketStart,
    source: 'supabase' as const,
  }));
}

export async function loadHeatCells(citySlug: string, period: PeriodKey): Promise<HeatCell[]> {
  if (!supabase) return [];

  const cityId = await getCityId(citySlug);
  if (!cityId) return [];

  const range = periodRange(period);
  const { data, error } = await supabase
    .from('oqf_heat_cells')
    .select('id,h3_index,bucket_start,predicted_density,observed_density,probability,confidence,sample_size')
    .eq('city_id', cityId)
    .gte('bucket_start', range.start.toISOString())
    .lt('bucket_start', range.end.toISOString())
    .order('bucket_start', { ascending: true })
    .limit(4000);

  if (error || !data) return [];
  return aggregateHeatRows(data as RawHeatRow[]);
}

export async function loadEvents(citySlug: string, period: PeriodKey): Promise<LiveEvent[]> {
  if (!supabase) return [];

  const cityId = await getCityId(citySlug);
  if (!cityId) return [];

  const { data: events, error: eventsError } = await supabase
    .from('oqf_events')
    .select('id,title,category,tags,official_url,primary_image_url,source_count,data_confidence')
    .eq('city_id', cityId)
    .neq('status', 'draft')
    .limit(500);

  if (eventsError || !events?.length) return [];

  const eventMap = new Map(events.map((event) => [String(event.id), event]));
  const eventIds = [...eventMap.keys()];
  const range = periodRange(period);

  const { data: occurrences, error: occurrencesError } = await supabase
    .from('oqf_event_occurrences')
    .select('id,event_id,venue_id,starts_at,ends_at,address_override,latitude,longitude,price_min,price_max,is_free,ticket_url,probability_score,confidence_score,heat_score,status')
    .in('event_id', eventIds)
    .neq('status', 'cancelled')
    .gte('starts_at', range.start.toISOString())
    .lt('starts_at', range.end.toISOString())
    .order('starts_at', { ascending: true })
    .limit(500);

  if (occurrencesError || !occurrences?.length) return [];

  const venueIds = [...new Set(
    occurrences.map((row) => row.venue_id).filter((value): value is string => Boolean(value)),
  )];

  const venueMap = new Map<string, { name: string | null; neighborhood: string | null; address: string | null; latitude: number | null; longitude: number | null }>();
  if (venueIds.length) {
    const { data: venues } = await supabase
      .from('oqf_venues')
      .select('id,name,neighborhood,address,latitude,longitude')
      .in('id', venueIds);

    for (const venue of venues ?? []) {
      venueMap.set(String(venue.id), {
        name: venue.name ?? null,
        neighborhood: venue.neighborhood ?? null,
        address: venue.address ?? null,
        latitude: venue.latitude == null ? null : Number(venue.latitude),
        longitude: venue.longitude == null ? null : Number(venue.longitude),
      });
    }
  }

  return occurrences.flatMap((row) => {
    const event = eventMap.get(String(row.event_id));
    if (!event) return [];

    const venue = row.venue_id ? venueMap.get(String(row.venue_id)) : undefined;
    const latitude = row.latitude == null ? venue?.latitude ?? null : Number(row.latitude);
    const longitude = row.longitude == null ? venue?.longitude ?? null : Number(row.longitude);

    return [{
      id: String(row.id),
      eventId: String(row.event_id),
      title: String(event.title),
      category: String(event.category ?? 'evento'),
      tags: Array.isArray(event.tags) ? event.tags.map(String) : [],
      startsAt: String(row.starts_at),
      endsAt: row.ends_at ? String(row.ends_at) : null,
      venueName: venue?.name ?? null,
      neighborhood: venue?.neighborhood ?? null,
      address: String(row.address_override ?? venue?.address ?? '') || null,
      position: latitude != null && longitude != null ? [longitude, latitude] as [number, number] : null,
      priceMin: row.price_min == null ? null : Number(row.price_min),
      priceMax: row.price_max == null ? null : Number(row.price_max),
      isFree: Boolean(row.is_free),
      ticketUrl: row.ticket_url ? String(row.ticket_url) : null,
      officialUrl: event.official_url ? String(event.official_url) : null,
      imageUrl: event.primary_image_url ? String(event.primary_image_url) : null,
      probability: Number(row.probability_score ?? 0),
      confidence: Number(row.confidence_score ?? event.data_confidence ?? 0),
      heatScore: Number(row.heat_score ?? 0),
      sourceCount: Number(event.source_count ?? 0),
    }];
  });
}
